/* eslint-disable indent */
import { Fleet } from '@src/main/modules/parameters/domain/fleet-positions/fleet.interface';
import { Position } from '@src/main/modules/parameters/domain/fleet-positions/position.interface';
import { CancelButton } from '@src/main/shared/ui/components/button/cancel/CancelButton';
import { SaveButton } from '@src/main/shared/ui/components/button/save/SaveButton';
import { Chip } from 'primereact/chip';
import { ListBox } from 'primereact/listbox';

interface ChipGroup {
  items: Position[];
  assigned?: boolean;
  onRemove: (p: Position) => void;
  labelPrefix?: string;
}

interface FleetAssignmentBodyProps {
  fleets: Fleet[];
  selectedFleet: Fleet | null;
  positionsNotAssigned: Position[];
  positionsToAdd: Position[];
  positionsToRemove: Position[];
  hasEditPermissions: boolean;
  loadingFleets: boolean;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  handleFleetChange: (e: any) => void;
  addPosition: (p: Position) => void;
  removePosition: (p: Position) => void;
  saveChanges: () => void;
  cancelChanges: () => void;
  updateFleetPositionsPending: boolean;
}

const FleetAssignmentBody = ({
  fleets,
  selectedFleet,
  positionsNotAssigned,
  positionsToAdd,
  positionsToRemove,
  hasEditPermissions,
  loadingFleets,
  handleFleetChange,
  addPosition,
  removePosition,
  saveChanges,
  cancelChanges,
  updateFleetPositionsPending,
}: FleetAssignmentBodyProps) => {
  const renderChips = ({ items, assigned = true, onRemove, labelPrefix }: ChipGroup) => {
    if (!items || items.length === 0) return null;

    return (
      <>
        {labelPrefix && selectedFleet?.fleet && (
          <p>
            {labelPrefix} <b>{selectedFleet.fleet}</b> los siguientes puestos:
          </p>
        )}
        <div className="fa-chips-area">
          {items.map((item) => (
            <div key={item.positionId} className="fa-chip-wrapper">
              <Chip
                label={item.position}
                removable={hasEditPermissions}
                removeIcon={assigned ? undefined : 'pi pi-plus-circle'}
                onRemove={() => {
                  onRemove(item);
                  return false;
                }}
                className={assigned ? 'assigned-chip' : 'unassigned-chip'}
              />
            </div>
          ))}
        </div>
      </>
    );
  };

  return (
    <>
      <div className="fa-row">
        <div className="fa-col fa-fleets">
          <h3 className="fa-header">Flotas</h3>
          <div className="fa-body">
            <div className="fa-listbox-wrapper">
              <ListBox
                value={selectedFleet}
                options={fleets}
                optionLabel="fleet"
                dataKey="fleetId"
                onChange={handleFleetChange}
                disabled={loadingFleets || !hasEditPermissions}
                className="fa-listbox"
              />
            </div>
          </div>
        </div>

        <div className="fa-col fa-assigned">
          <h3 className="fa-header">Puestos asignados</h3>
          <div className="fa-body">
            {renderChips({ items: selectedFleet?.positions ?? [], onRemove: removePosition })}
          </div>
        </div>

        <div className="fa-col fa-unassigned">
          <h3 className="fa-header">Puestos sin asignar</h3>
          <div className="fa-body">
            {renderChips({ items: positionsNotAssigned, onRemove: addPosition, assigned: false })}
          </div>
        </div>
      </div>

      {selectedFleet?.fleet && !loadingFleets && (
        <>
          {renderChips({
            items: positionsToAdd,
            onRemove: removePosition,
            labelPrefix: 'Se van a añadir a la flota',
          })}
          {renderChips({
            items: positionsToRemove,
            onRemove: addPosition,
            assigned: false,
            labelPrefix: 'Se van a eliminar de la flota',
          })}
        </>
      )}

      {hasEditPermissions && (
        <div className="fa-actions">
          <CancelButton onClick={cancelChanges} />
          <SaveButton onClick={saveChanges} loading={updateFleetPositionsPending} />
        </div>
      )}
    </>
  );
};

export default FleetAssignmentBody;
